import { useCallback, useMemo, useRef, useState, useTransition } from "react";

import { useDeepCompareEffect, useSyncedRef } from "@skydio/rivet-ui";

import { useConnectionNodes } from "../connections";

import type { KeyType } from "react-relay/relay-hooks/helpers";
import type { usePaginationFragmentHookType } from "react-relay/relay-hooks/usePaginationFragment";
import type { OperationType, VariablesOf } from "relay-runtime";
import type { PaginationInterface } from "@skydio/rivet-ui";
import type { Connection, Edge } from "../connections";

/**
 * Takes the output of a Relay pagination fragment and returns the necessary data and methods to
 * render a paginated UI. If variables are provided, the fragment will be refetched when they change.
 *
 * @param paginationData The output of a `usePaginationFragment` hook
 * @param getConnection A function that extracts the connection data from the fragment data
 * @param pageSize The number of items to display per page
 * @param variables (Optional) The variables to use when refetching the fragment
 *
 * @returns A tuple containing:
 * - A pagination interface, which can be spread as props onto a Rivet UI `Pagination` component
 * - The current page of data
 * - A boolean indicating whether the fragment is currently refetching
 */
export const usePagination = <
  TQuery extends OperationType,
  TData,
  TKey extends KeyType<TData>,
  TConnectionData,
>(
  paginationData: usePaginationFragmentHookType<TQuery, TKey, TData>,
  getConnection: (data: TData) => Connection<Edge<TConnectionData>> | null | undefined,
  pageSize = 10,
  variables: Partial<VariablesOf<TQuery>> = {}
) => {
  const { data, hasNext, loadNext, refetch, isLoadingNext, isLoadingPrevious } = paginationData;

  const getConnectionRef = useSyncedRef(getConnection);

  const connection = useMemo(() => {
    return getConnectionRef.current(data);
  }, [getConnectionRef, data]);

  const nodes = useConnectionNodes(connection);

  const [pageNumber, setPageNumber] = useState(0);

  const resetPageNumber = useCallback(() => setPageNumber(0), [setPageNumber]);

  const currentPageData = useMemo(
    () => nodes.slice(pageSize * pageNumber, pageSize * (pageNumber + 1)),
    [nodes, pageNumber, pageSize]
  );

  const loadedItemCount = nodes.length;
  const hasLoadedNextPage = loadedItemCount > pageSize * (pageNumber + 1);
  const hasNextPage = hasNext || hasLoadedNextPage;

  const incrementPage = useCallback(() => {
    if (!hasNextPage) {
      return;
    }

    if (hasLoadedNextPage) {
      setPageNumber(pageNumber + 1);
    } else if (hasNext) {
      loadNext(pageSize, { onComplete: () => setPageNumber(pageNumber + 1) });
    }
  }, [hasNextPage, hasLoadedNextPage, hasNext, loadNext, pageSize, pageNumber, setPageNumber]);

  const decrementPage = useCallback(() => {
    setPageNumber(Math.max(pageNumber - 1, 0));
  }, [pageNumber, setPageNumber]);

  const pagination: PaginationInterface = {
    pageNumber,
    pageSize,
    totalCount: connection?.totalCount ?? 0,
    hasPreviousPage: pageNumber > 0,
    hasNextPage,
    decrementPage,
    incrementPage,
    resetPageNumber,
    isLoadingNext,
    isLoadingPrevious,
  };

  const [isRefetching, startTransition] = useTransition();

  const hasMountedRef = useRef(false);

  // If variables are provided, refetch the fragment when they change and reset the page number
  useDeepCompareEffect(() => {
    if (hasMountedRef.current) {
      startTransition(() => {
        refetch(variables, { onComplete: resetPageNumber });
      });
    }
    hasMountedRef.current = true;
  }, [variables]);

  return [pagination, currentPageData, isRefetching] as const;
};
